"use client";

import HardNavLink from "@/components/shared/HardNavLink";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import {
  Home, Compass, BookOpen, Star, FileText,
  MessageSquare, Mic, Trophy, Shield, X,
  ChevronUp, User, Route, BarChart2, Award,
  Settings, CreditCard, HelpCircle, LogOut, UserPlus, Gem, Info,
} from "lucide-react";
import { useState } from "react";

import type { AppUser } from "./AppShell";
import { useAuth } from "@/lib/AuthContext";

const PUBLIC_NAV = [
  { label: "Trang chủ",      icon: Home,          href: "/" },
  { label: "Khám phá",       icon: Compass,       href: "/explore" },
  { label: "Lộ trình",       icon: BookOpen,       href: "/paths" },
  { label: "Đề thi",         icon: FileText,       href: "/exams" },
  { label: "Review",         icon: MessageSquare,  href: "/reviews" },
  { label: "JB Interview",   icon: Mic,            href: "/interview/new" },
  { label: "Bảng xếp hạng", icon: Trophy,         href: "/leaderboard" },
] as const;

const AUTH_NAV = [
  { label: "Creator Studio",     icon: Star,     href: "/studio" },
  { label: "Giới thiệu bạn bè",  icon: UserPlus, href: "/invite" },
] as const;

// Thêm array mới này — hiện với mọi user (cả guest)
const FOOTER_NAV = [
  { label: "Giới thiệu",         icon: Info,     href: "/gioi-thieu" },
] as const;

interface SidebarProps {
  collapsed: boolean;
  mobileOpen: boolean;
  onMobileClose: () => void;
  isAdmin?: boolean;
  user?: AppUser | null;
  onOpenAuth?: () => void;
}

const USER_MENU: Array<{ href: string; label: string; icon: any }> = [
  { href: "__profile__", label: "Trang cá nhân",      icon: User },
  { href: "/my-paths",   label: "Quản lý lộ trình",   icon: Route },
  { href: "/badges",     label: "Huy hiệu",           icon: Award },
  { href: "/settings",   label: "Cài đặt tài khoản",  icon: Settings },
  { href: "/wallet",     label: "Nạp tiền - Rút tiền", icon: Gem },
  { href: "/help",       label: "Trợ giúp",            icon: HelpCircle },
];

export default function Sidebar({
  collapsed,
  mobileOpen,
  onMobileClose,
  isAdmin = false,
  user,
  onOpenAuth,
}: SidebarProps) {
  const pathname = usePathname();
  const [isMainMenuOpen, setIsMainMenuOpen] = useState(true);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const toggleMainMenu = () => {
    const next = !isMainMenuOpen;
    setIsMainMenuOpen(next);
    if (next) setIsUserMenuOpen(false);
  };

  const toggleUserMenu = () => {
    const next = !isUserMenuOpen;
    setIsUserMenuOpen(next);
    if (next) setIsMainMenuOpen(false);
  };

  function isActive(href: string): boolean {
    if (href === "/") return pathname === "/";
    return pathname.startsWith(href);
  }

  const allNav = [...PUBLIC_NAV, ...(user ? AUTH_NAV : []), ...FOOTER_NAV];

  return (
    <>
      {/* Mobile overlay */}
      <div
        className={cn(
          "fixed inset-0 z-40 bg-teal-900/55 backdrop-blur-[2px] transition-opacity md:hidden",
          mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={onMobileClose}
      />

      {/* Sidebar */}
      <aside
        className={cn(
          "flex flex-col h-full bg-teal-800 overflow-hidden flex-shrink-0 transition-all duration-[280ms] ease-[cubic-bezier(.4,0,.2,1)]",
          collapsed ? "w-0 min-w-0" : "w-60 min-w-[15rem]",
          "max-md:fixed max-md:inset-y-0 max-md:left-0 max-md:z-50 max-md:w-60 max-md:min-w-[15rem]",
          mobileOpen ? "max-md:translate-x-0" : "max-md:-translate-x-full"
        )}
      >
        {/* Logo */}
        <div className="flex-shrink-0 px-5 pt-[1.375rem] pb-[1.125rem] border-b border-white/[.08]">
          <div className="flex items-center justify-between">
            <HardNavLink href="/" onClick={onMobileClose}>
              <div className="font-display font-extrabold text-white text-[1.25rem] tracking-tight leading-none">
                JB <span className="text-orange-400">AI</span>
              </div>
              <div className="text-[0.625rem] text-white/35 mt-[3px] tracking-[0.5px]">
                Nền tảng học thi thông minh
              </div>
            </HardNavLink>
            <button
              onClick={onMobileClose}
              className="md:hidden w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/15 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Navigation — collapsible on mobile */}
        <nav className="flex-shrink-0 px-3 pt-4 pb-2">
          <button onClick={toggleMainMenu} className="w-full flex items-center justify-between px-2 mb-2 group">
            <span className="text-[0.5625rem] font-bold text-white/30 tracking-[1.5px] uppercase">Menu chính</span>
            <ChevronUp className={`w-3 h-3 text-white/30 transition-transform duration-200 ${isMainMenuOpen ? "" : "rotate-180"}`} />
          </button>

          <div className={`overflow-hidden transition-all duration-300 ${isMainMenuOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"}`}>
            {allNav.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);

              return (
                <HardNavLink
                  key={item.href}
                  href={item.href}
                  className={cn(
                    "w-full flex items-center gap-[10px] px-3 py-[9px] rounded-lg text-[13px] font-medium cursor-pointer transition-all mb-[2px]",
                    active
                      ? "bg-orange-500/20 text-orange-400"
                      : "text-white/60 hover:bg-white/[.07] hover:text-white"
                  )}
                  onClick={onMobileClose}
                >
                  <Icon className="w-[15px] h-[15px] flex-shrink-0 opacity-90" />
                  <span className="truncate">{item.label}</span>
                </HardNavLink>
              );
            })}

            {isAdmin && (
              <HardNavLink
                href="/admin"
                className={cn(
                  "w-full flex items-center gap-[10px] px-3 py-[9px] rounded-lg text-[13px] font-medium cursor-pointer transition-all mb-[2px]",
                  pathname.startsWith("/admin")
                    ? "bg-orange-500/20 text-orange-400"
                    : "text-white/60 hover:bg-white/[.07] hover:text-white"
                )}
                onClick={onMobileClose}
              >
                <Shield className="w-[15px] h-[15px] flex-shrink-0 opacity-90" />
                <span className="truncate">Admin</span>
              </HardNavLink>
            )}
          </div>
        </nav>

        {/* Recent — placeholder */}
        <div className="flex-1 overflow-y-auto px-3 pt-3 border-t border-white/[.06] scrollbar-thin">
        </div>

        {/* User Menu — pinned bottom, mobile only */}
        {user && (
          <div className="block md:hidden mt-auto shrink-0 border-t border-white/10">
            {/* Drop-up menu */}
            <div className={`overflow-hidden transition-all duration-300 ${isUserMenuOpen ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"}`}>
              <div className="px-3 py-2 flex flex-col gap-0.5">
                {USER_MENU.map(item => {
                  const Icon = item.icon;
                  const href = item.href === "__profile__"
                    ? (user.username ? `/${user.username}` : `/${user.id}`)
                    : item.href;
                  return (
                    <HardNavLink
                      key={item.href}
                      href={href}
                      onClick={() => { onMobileClose(); setIsUserMenuOpen(false); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[12px] font-medium text-white/70 hover:bg-white/10 hover:text-white transition-colors text-left"
                    >
                      <Icon className="w-4 h-4 shrink-0 opacity-70" />
                      <span className="truncate">{item.label}</span>
                    </HardNavLink>
                  );
                })}
                <form action="/auth/signout" method="post">
                  <button type="submit"
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[12px] font-medium text-red-400 hover:bg-red-500/10 transition-colors text-left">
                    <LogOut className="w-4 h-4 shrink-0 opacity-70" />
                    <span>Đăng xuất</span>
                  </button>
                </form>
              </div>
            </div>

            {/* User button */}
            <button onClick={toggleUserMenu}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors">
              {user.avatar_url ? (
                <img src={user.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center text-white text-[10px] font-extrabold shrink-0">
                  {(user.full_name || user.username || "U").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0 text-left">
                <div className="text-[12px] font-bold text-white truncate">{user.full_name || user.username || "User"}</div>
                <div className="text-[10px] text-white/40 truncate">{user.email}</div>
              </div>
              <ChevronUp className={`w-4 h-4 text-white/40 shrink-0 transition-transform duration-200 ${isUserMenuOpen ? "" : "rotate-180"}`} />
            </button>
          </div>
        )}
      </aside>
    </>
  );
}